/**
 * @file src/config/userDefaultPair.js
 * @description The orbiter and world a person has chosen as their own default pair.
 *
 * There is one resolution order and it is the same everywhere, for every kind of content:
 *
 *   1. the pair the content itself carries, when it is available — an author's pairing is part of
 *      the work and is never overridden;
 *   2. THIS, the signed-in person's own pair, from `playback` in their account settings;
 *   3. the platform's pair.
 *
 * So this module answers rung 2, and only rung 2. It does not decide anything: the sites that
 * already fall back — the assembler when a track carries no orbiter or its orbiter has gone away,
 * the local library when a file on this machine never had one — ask it before they reach for the
 * platform's answer.
 *
 * Read once per session and shared. It is asked for on a boot path, and a settings fetch per voice
 * would put the account on the critical path of every card. Ask for it LAZILY, at the moment a
 * fallback is actually needed: content that carries its own live pair must never wait on this.
 *
 * A guest, a blocked request and an account that never opened the picker all answer the same empty
 * pair — which is exactly the shape "rung 2 has nothing to say" already had, so every caller keeps
 * the behaviour it has today until somebody chooses.
 */
import { fetchUserSettings } from '../api/settingsApi.js';

/** Nobody chose: the caller carries on to the platform's pair, as it did before this existed. */
const EMPTY_PAIR = Object.freeze({ orbiterId: null, worldId: null });

let resolved = null;
let pending = null;
let generation = 0;

function readId(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * The person's default pair, fetched once and remembered for the session.
 * @returns {Promise<{ orbiterId: string|null, worldId: string|null }>}
 */
export async function resolveUserDefaultPair() {
  if (resolved) return resolved;
  if (!pending) {
    const startedAt = generation;
    const request = fetchUserSettings()
      .then((settings) => {
        // `fetchUserSettings` answers null for a guest AND for a read that failed — a dropped
        // connection, a 401 while a token is being refreshed. Only an answer that actually came
        // back is remembered: latching the empty pair on a blip would ignore somebody's chosen
        // orbiter for the whole session, and the next fallback is a cheap place to ask again.
        if (!settings || typeof settings !== 'object') return { pair: EMPTY_PAIR, answered: false };
        const playback = settings.playback;
        if (!playback || typeof playback !== 'object') return { pair: EMPTY_PAIR, answered: true };
        const orbiterId = readId(playback.defaultOrbiterId);
        const worldId = readId(playback.defaultEntangledWorldId);
        const pair = orbiterId || worldId ? Object.freeze({ orbiterId, worldId }) : EMPTY_PAIR;
        return { pair, answered: true };
      })
      // The belt for anything `fetchUserSettings` cannot swallow itself. A pair that could not be
      // read is not an error anybody should see — it is one rung of three staying quiet.
      .catch(() => ({ pair: EMPTY_PAIR, answered: false }))
      .then(({ pair, answered }) => {
        if (pending === request) pending = null;
        // An identity reset while the settings request was in flight makes its answer belong to the
        // previous person. Ask again instead of handing that answer to the caller that outlived it.
        if (startedAt !== generation) return resolveUserDefaultPair();
        if (answered) resolved = pair;
        return pair;
      });
    pending = request;
  }
  return pending;
}

/** The pair already in hand, without asking for one. Empty until `resolveUserDefaultPair` lands. */
export function readUserDefaultPair() {
  return resolved || EMPTY_PAIR;
}

/** Whether the settings endpoint answered, including the valid answer "no pair chosen". */
export function hasResolvedUserDefaultPair() {
  return resolved !== null;
}

/** Forget the session's answer — the tests, and a sign-in that changes who is asking. */
export function resetUserDefaultPair() {
  generation += 1;
  resolved = null;
  pending = null;
}
